import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    AssistantFeedback,
    AssistantJob,
    AssistantStatus,
    askAssistant,
    getAssistantJob,
    getAssistantStatus,
    sendAssistantFeedback,
} from '../../api/assistant';
import { ApiError, CODE_ABORTED, CODE_TIMEOUT } from '../../api/request';
import { locate } from '../layout/menus';
import MiniMarkdown from './MiniMarkdown';
import { suggestionsFor } from './suggestions';
import { RESIZE_HANDLES, useFloatingBox } from './useFloatingBox';

// 平台助手 (AI 使用答疑, 2026-10-14, doc/平台助手/设计-20261014.md):
// 顶栏「✦ AI 助手」→ 对话框: 默认贴右侧并把页面往左让出位置 (不挡列表操作, 可一边问一边操作), 拖左边改宽;
// 拖标题栏变浮动窗口 (可移动、拖边角缩放, 叠在页面上), 双击标题栏或 ⇥ 贴回; 位置尺寸本机记住. 解答"平台怎么用", 并可只读查询本人有权限的已发布数据 (后端把关, 不含人员信息); 不修改任何数据.
// 只把当前页面的路由模板与菜单名带给后端 (不带路径参数与页面内容).
// 对话只留在本次打开的页面里 (挂在顶栏, 切页面不丢); 刷新页面即新对话.
// 视觉沿用工艺编排 ✦ AI 面板的语言 (violet + ✦ 是 AI 的专属标识): 紫色左边条的结果卡片、灰色提问气泡、转圈计秒的进度、聊天式输入条.

const POLL_MS = 800;
const MAX_QUESTION = 1000;
const COMPOSER_MAX_H = 140;
const SEEN_KEY = 'assistant:seen';
const BOX_KEY = 'assistant:box';
const DOCKED_WIDTH = 440;
// 贴边让位后页面至少留这么宽, 不够就叠在页面上 (小屏)
const MIN_PAGE_WIDTH = 720;
// 让位时少让这么多: 面板盖住页面右边距 (p-6) 的一部分, 页面与面板之间只留窄缝
const PAGE_PAD_OVERLAP = 14;

interface Turn {
    jobId: string;
    question: string;
    answer: string;
    status: AssistantJob['status'];
    error?: string;
    feedback: AssistantFeedback;
    elapsedMs: number;
    /** 生成中正在查询的数据 (中文名) */
    querying?: string;
    /** 发起时刻 (本地, 用于生成中计秒) */
    startedAt: number;
}

// 后端业务错误形如 "请求参数不正确|请输入问题", 只给人看后半句
const errText = (e: unknown) =>
    e instanceof ApiError ? e.message.split('|').pop() || e.message : String(e);

// 切页面时前端会统一取消在途的读请求 (含轮询), 这类不算失败, 接着轮询即可
const isTransient = (e: unknown) =>
    e instanceof ApiError &&
    (e.code === CODE_ABORTED || e.code === CODE_TIMEOUT);

const toTurn = (job: AssistantJob): Turn => ({
    jobId: job.jobId,
    question: job.question,
    answer: job.answer,
    status: job.status,
    error: job.error,
    feedback: job.feedback,
    elapsedMs: job.elapsedMs,
    querying: job.querying,
    startedAt: Date.now(),
});

const AiMark = ({ size = 'md' }: { size?: 'sm' | 'md' }) => (
    <span
        className={`flex items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 via-violet-500 to-fuchsia-500 text-white shadow-sm shrink-0 ${
            size === 'sm' ? 'w-5 h-5 text-[10px]' : 'w-7 h-7 text-[13px]'
        }`}
    >
        ✦
    </span>
);

/** 生成中: 转圈 + 说明 (查询中显示查什么) + 已用秒数 */
const Progress = ({
    startedAt,
    hasText,
    querying,
}: {
    startedAt: number;
    hasText: boolean;
    querying?: string;
}) => {
    const [sec, setSec] = useState(0);
    useEffect(() => {
        const t = window.setInterval(
            () => setSec(Math.floor((Date.now() - startedAt) / 1000)),
            1000,
        );
        return () => window.clearInterval(t);
    }, [startedAt]);
    return (
        <div className="flex items-center gap-2 text-violet-600 text-xs">
            <span className="inline-block w-3.5 h-3.5 rounded-full border-2 border-violet-300 border-t-violet-600 animate-spin shrink-0" />
            <span>
                {querying
                    ? `AI 正在查询数据: ${querying}`
                    : hasText
                      ? 'AI 正在作答'
                      : 'AI 正在思考'}
            </span>
            {sec > 0 && (
                <span className="ml-auto text-slate-400 tabular-nums">
                    {sec}s
                </span>
            )}
        </div>
    );
};

/** 一轮回答: 紫色左边条卡片, 头部「✦ 解答 · 依据平台使用指引 · 耗时」, 底部评价 */
const AnswerCard = ({
    t,
    onRate,
}: {
    t: Turn;
    onRate: (helpful: boolean, note?: string) => void;
}) => {
    const [asking, setAsking] = useState(false);
    const [note, setNote] = useState('');
    const running = t.status === 'running';
    return (
        <div className="rounded-lg border border-violet-200 border-l-4 border-l-violet-500 bg-violet-50/40">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-violet-100 text-xs whitespace-nowrap">
                <span className="font-semibold text-violet-700">✦ 解答</span>
                <span className="text-violet-600">
                    · 依据使用指引与已发布数据
                </span>
                {t.status === 'ok' && t.elapsedMs > 0 && (
                    <span className="text-slate-400">
                        · {(t.elapsedMs / 1000).toFixed(1)}s
                    </span>
                )}
            </div>
            <div className="px-3 py-2.5 text-[13px] text-slate-700 space-y-2">
                {running && (
                    <Progress
                        startedAt={t.startedAt}
                        hasText={!!t.answer}
                        querying={t.querying}
                    />
                )}
                {t.answer && <MiniMarkdown text={t.answer} />}
                {t.status === 'error' && (
                    <div className="text-xs text-rose-600">
                        {t.error || '生成失败, 请重试'}
                    </div>
                )}
            </div>
            {t.status === 'ok' && (
                <div className="px-3 py-2 border-t border-violet-100 space-y-2">
                    <div className="flex items-center gap-2">
                        {t.feedback === 'none' ? (
                            <>
                                <button
                                    type="button"
                                    className="h-7 px-2.5 rounded-md border border-slate-200 bg-white text-xs text-slate-600 hover:border-violet-300 hover:text-violet-700"
                                    onClick={() => onRate(true)}
                                >
                                    👍 有帮助
                                </button>
                                <button
                                    type="button"
                                    className={`h-7 px-2.5 rounded-md border bg-white text-xs hover:border-violet-300 hover:text-violet-700 ${
                                        asking
                                            ? 'border-violet-300 text-violet-700'
                                            : 'border-slate-200 text-slate-600'
                                    }`}
                                    onClick={() => setAsking((v) => !v)}
                                >
                                    👎 没帮上
                                </button>
                            </>
                        ) : (
                            <span className="tag bg-white text-slate-500 border border-slate-200">
                                {t.feedback === 'up'
                                    ? '✓ 已反馈有帮助'
                                    : '✓ 已反馈, 我们会据此补充说明'}
                            </span>
                        )}
                        <span className="ml-auto text-[11px] text-slate-400">
                            以平台实际页面为准
                        </span>
                    </div>
                    {asking && t.feedback === 'none' && (
                        <div className="flex gap-2">
                            <input
                                className="flex-1 min-w-0 h-7 rounded-md border border-slate-200 bg-white px-2 text-xs outline-none focus:border-violet-300"
                                placeholder="哪里没帮上? (可不填)"
                                maxLength={200}
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                            />
                            <button
                                type="button"
                                className="h-7 px-3 rounded-md bg-violet-600 text-white text-xs font-medium hover:bg-violet-700"
                                onClick={() => onRate(false, note)}
                            >
                                提交
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

/** 底部聊天式输入条: 随内容长高; Enter 发送, Shift+Enter 换行 */
const Composer = ({
    value,
    onChange,
    onSend,
    busy,
    disabled,
}: {
    value: string;
    onChange: (v: string) => void;
    onSend: () => void;
    busy: boolean;
    disabled: boolean;
}) => {
    const ref = useRef<HTMLTextAreaElement>(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, COMPOSER_MAX_H)}px`;
        el.style.overflowY =
            el.scrollHeight > COMPOSER_MAX_H ? 'auto' : 'hidden';
    }, [value]);
    const canSend = !busy && !disabled && !!value.trim();
    return (
        <div className="rounded-lg border border-slate-200 bg-white px-3 pt-2 pb-1.5 focus-within:border-violet-300">
            <textarea
                ref={ref}
                rows={2}
                className="block w-full resize-none overflow-hidden border-0 bg-transparent p-0 text-[13px] leading-5 text-slate-700 placeholder:text-slate-400 outline-none focus:outline-none focus:ring-0"
                style={{ minHeight: 40 }}
                placeholder="问问平台怎么用或查数据, 如: 某材料在某设备上的推荐参数?"
                maxLength={MAX_QUESTION}
                value={value}
                disabled={disabled}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={(e) => {
                    if (
                        e.key === 'Enter' &&
                        !e.shiftKey &&
                        !e.nativeEvent.isComposing
                    ) {
                        e.preventDefault();
                        if (canSend) onSend();
                    }
                }}
            />
            <div className="flex items-center gap-2 mt-1">
                <span className="text-[11px] text-slate-400">
                    Enter 发送 · Shift+Enter 换行
                </span>
                <button
                    type="button"
                    className="ml-auto inline-flex items-center gap-1 h-7 px-3 rounded-md bg-violet-600 text-white text-xs font-medium hover:bg-violet-700 disabled:opacity-40 disabled:hover:bg-violet-600"
                    disabled={!canSend}
                    onClick={onSend}
                >
                    {busy ? (
                        <>
                            <span className="inline-block w-3 h-3 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                            回答中
                        </>
                    ) : (
                        '✦ 发送'
                    )}
                </button>
            </div>
        </div>
    );
};

const AssistantPanel = () => {
    const { pathname } = useLocation();
    const [open, setOpen] = useState(false);
    // 是否打开过 (本机记住, 打开过就不再显示新功能呼吸点); 读写失败按没打开过处理
    const [everOpened, setEverOpened] = useState(() => {
        try {
            return window.localStorage.getItem(SEEN_KEY) === '1';
        } catch {
            return false;
        }
    });
    const [status, setStatus] = useState<AssistantStatus | null>(null);
    const [turns, setTurns] = useState<Turn[]>([]);
    const [threadId, setThreadId] = useState<string>();
    const [input, setInput] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const bodyRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLElement>(null);
    const alive = useRef(true);
    const { box, floating, start, dock } = useFloatingBox(
        BOX_KEY,
        DOCKED_WIDTH,
    );

    const loc = locate(pathname);
    const sub = loc ? loc.menu.subs[loc.subIdx] : undefined;
    // 路由模板: 子页登记的路径 (详情页也是占位路径, 不含 xcode 等参数)
    const route = sub?.path || loc?.menu.path;
    const routeName = [loc?.menu.label, sub?.label].filter(Boolean).join(' / ');

    // 挂载时置真 (StrictMode 下 effect 会 挂载→卸载→再挂载, 只在卸载时置假会让轮询一开始就退出)
    useEffect(() => {
        alive.current = true;
        return () => {
            alive.current = false;
        };
    }, []);

    const refreshStatus = useCallback(async () => {
        try {
            setStatus(await getAssistantStatus());
        } catch (e) {
            setStatus({ enabled: false, reason: errText(e) });
        }
    }, []);

    useEffect(() => {
        if (!open) return;
        refreshStatus();
        if (!everOpened) {
            setEverOpened(true);
            try {
                window.localStorage.setItem(SEEN_KEY, '1');
            } catch {
                // 隐私模式等写不进去, 只影响呼吸点
            }
        }
    }, [open, refreshStatus, everOpened]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open]);

    // 滚动不穿透到后面的页面: 抽屉里能往这个方向滚的元素 (对话区 / 输入框) 照常滚,
    // 否则 (滚到头 / 内容不够长 / 在标题栏、底栏上滚) 直接吞掉, 不让后面的列表跟着动.
    // React 的 onWheel 是 passive 的 preventDefault 无效, 所以挂原生监听.
    useEffect(() => {
        const panel = panelRef.current;
        if (!open || !panel) return undefined;
        const onWheel = (e: WheelEvent) => {
            if (e.deltaY === 0) return;
            for (
                let el = e.target as HTMLElement | null;
                el && el !== panel;
                el = el.parentElement
            ) {
                const oy = window.getComputedStyle(el).overflowY;
                if (
                    (oy === 'auto' || oy === 'scroll') &&
                    el.scrollHeight > el.clientHeight
                ) {
                    const canScroll =
                        e.deltaY < 0
                            ? el.scrollTop > 0
                            : el.scrollTop + el.clientHeight <
                              el.scrollHeight - 1;
                    if (canScroll) return;
                }
            }
            e.preventDefault();
        };
        panel.addEventListener('wheel', onWheel, { passive: false });
        return () => panel.removeEventListener('wheel', onWheel);
    }, [open]);

    // 贴边时给页面让位: body 右侧留出面板宽度, 页面 (含顶栏) 跟着变窄, 不被遮挡
    const reserve =
        open && !floating && window.innerWidth - box.w >= MIN_PAGE_WIDTH
            ? box.w - PAGE_PAD_OVERLAP
            : 0;
    useEffect(() => {
        if (!reserve) return undefined;
        const body = document.body.style;
        const prev = body.paddingRight;
        body.paddingRight = `${reserve}px`;
        return () => {
            body.paddingRight = prev;
        };
    }, [reserve]);

    // 有新内容就滚到底
    useEffect(() => {
        bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight });
    }, [turns]);

    const patchTurn = (jobId: string, patch: Partial<Turn>) =>
        setTurns((ts) =>
            ts.map((t) => (t.jobId === jobId ? { ...t, ...patch } : t)),
        );

    const poll = useCallback(async (jobId: string) => {
        for (;;) {
            if (!alive.current) return;
            let job: AssistantJob;
            try {
                job = await getAssistantJob(jobId);
            } catch (e) {
                if (isTransient(e)) {
                    await new Promise((r) => window.setTimeout(r, POLL_MS));
                    continue;
                }
                patchTurn(jobId, { status: 'error', error: errText(e) });
                return;
            }
            patchTurn(jobId, {
                answer: job.answer,
                status: job.status,
                error: job.error,
                feedback: job.feedback,
                elapsedMs: job.elapsedMs,
                querying: job.querying,
            });
            if (job.status !== 'running') return;
            await new Promise((r) => window.setTimeout(r, POLL_MS));
        }
    }, []);

    const send = async (raw: string) => {
        const question = raw.trim();
        if (!question || busy) return;
        setBusy(true);
        setError('');
        try {
            const job = await askAssistant({
                question,
                threadId,
                route,
                routeName,
            });
            setThreadId(job.threadId);
            setInput('');
            setTurns((ts) => [...ts, toTurn(job)]);
            if (job.status === 'running') await poll(job.jobId);
        } catch (e) {
            setError(errText(e));
        } finally {
            setBusy(false);
        }
    };

    const rate = async (jobId: string, helpful: boolean, note?: string) => {
        try {
            await sendAssistantFeedback({ jobId, helpful, note });
            patchTurn(jobId, { feedback: helpful ? 'up' : 'down' });
        } catch (e) {
            setError(errText(e));
        }
    };

    const newChat = () => {
        if (busy) return;
        setTurns([]);
        setThreadId(undefined);
        setError('');
    };

    const unavailable =
        status && !status.enabled ? status.reason || '助手暂不可用' : '';

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                title="AI 平台助手: 解答平台怎么用, 可查询你有权限的已发布数据 (只读)"
                className={`relative inline-flex items-center gap-1.5 h-8 pl-2.5 pr-3.5 rounded-full text-[13px] font-medium text-white shadow-md shadow-indigo-500/30 transition-[filter] duration-150 hover:brightness-110 bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500 ${
                    open ? 'ring-2 ring-offset-2 ring-violet-300' : ''
                }`}
            >
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-white/20 text-[12px]">
                    ✦
                </span>
                AI 助手
                {/* 首次打开前带一个呼吸点, 提示这是新功能 */}
                {!everOpened && (
                    <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75 animate-ping" />
                        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-400 ring-2 ring-white" />
                    </span>
                )}
            </button>
            {open && (
                <aside
                    ref={panelRef}
                    style={{
                        left: box.x,
                        top: box.y,
                        width: box.w,
                        height: box.h,
                    }}
                    className={`fixed bg-white z-50 flex flex-col text-[13px] ${
                        floating
                            ? 'rounded-xl border border-violet-200 overflow-hidden shadow-2xl'
                            : reserve
                              ? 'border-l border-violet-200'
                              : 'border-l border-violet-100 shadow-2xl'
                    }`}
                >
                    {RESIZE_HANDLES.filter(
                        (h) => floating || h.kind === 'w',
                    ).map((h) => (
                        <div
                            key={h.kind}
                            className={`absolute z-10 touch-none ${h.className}`}
                            onPointerDown={start(h.kind)}
                        />
                    ))}
                    <div
                        className="h-14 px-4 flex items-center gap-2.5 border-b border-violet-100 bg-gradient-to-r from-violet-50/80 to-white cursor-move select-none touch-none shrink-0"
                        title={
                            floating
                                ? '拖动可移动, 拖边角可缩放, 双击贴回右侧'
                                : '拖动变为浮动窗口; 拖左边可调宽度'
                        }
                        onPointerDown={start('move')}
                        onDoubleClick={(e) => {
                            if (!(e.target as HTMLElement).closest('button'))
                                dock();
                        }}
                    >
                        <AiMark />
                        <div className="min-w-0">
                            <div className="font-semibold text-slate-900 leading-5">
                                AI 平台助手
                            </div>
                            <div
                                className="text-[11px] text-slate-500 leading-4 truncate"
                                title="解答平台怎么用, 可只读查询你有权限的已发布数据"
                            >
                                解答使用问题 · 可查已发布数据
                            </div>
                        </div>
                        <button
                            type="button"
                            className="ml-auto shrink-0 whitespace-nowrap inline-flex items-center gap-1 h-7 px-2.5 rounded-md border border-violet-200 bg-white text-xs font-medium text-violet-700 shadow-sm hover:bg-violet-50 hover:border-violet-300 disabled:opacity-40 disabled:hover:bg-white"
                            title="清空对话, 下一次发送开始新对话"
                            onClick={newChat}
                            disabled={busy}
                        >
                            <span className="text-sm leading-none">＋</span>
                            新对话
                        </button>
                        {floating && (
                            <button
                                type="button"
                                className="shrink-0 w-7 h-7 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-700 text-sm"
                                onClick={dock}
                                aria-label="贴回右侧"
                                title="贴回右侧"
                            >
                                ⇥
                            </button>
                        )}
                        <button
                            type="button"
                            className="shrink-0 w-7 h-7 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-700 text-sm"
                            onClick={() => setOpen(false)}
                            aria-label="关闭"
                            title="关闭 (对话会保留, 再打开接着聊)"
                        >
                            ✕
                        </button>
                    </div>

                    <div
                        ref={bodyRef}
                        className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4 space-y-4"
                    >
                        {turns.length === 0 && (
                            <div className="space-y-3">
                                <div className="rounded-lg border border-violet-100 bg-violet-50/40 px-3 py-2.5 text-xs text-slate-600 leading-relaxed">
                                    你好,
                                    我是平台助手。可以问我某个功能在哪、怎么操作、为什么没生效、需要什么权限。
                                    {routeName && (
                                        <>
                                            <br />
                                            你现在在「
                                            <span className="text-violet-700">
                                                {routeName}
                                            </span>
                                            」, 可以试试:
                                        </>
                                    )}
                                </div>
                                <div className="space-y-1.5">
                                    {suggestionsFor(loc?.menu.key).map((q) => (
                                        <button
                                            key={q}
                                            type="button"
                                            disabled={busy || !!unavailable}
                                            onClick={() => send(q)}
                                            className="group flex w-full items-center gap-2 text-left px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 hover:border-violet-300 hover:bg-violet-50/50 disabled:opacity-50"
                                        >
                                            <span className="text-violet-400 group-hover:text-violet-600">
                                                ✦
                                            </span>
                                            <span className="min-w-0">{q}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {turns.map((t) => (
                            <div key={t.jobId} className="space-y-2">
                                <div className="flex justify-end">
                                    <div className="max-w-[85%] rounded-lg bg-slate-100 px-3 py-2 text-[13px] text-slate-700 whitespace-pre-wrap">
                                        {t.question}
                                    </div>
                                </div>
                                <AnswerCard
                                    t={t}
                                    onRate={(helpful, note) =>
                                        rate(t.jobId, helpful, note)
                                    }
                                />
                            </div>
                        ))}
                    </div>

                    <div className="px-3 py-3 border-t border-violet-100 space-y-1.5">
                        {(error || unavailable) && (
                            <div className="text-xs text-rose-600 px-1">
                                {error || unavailable}
                            </div>
                        )}
                        <Composer
                            value={input}
                            onChange={setInput}
                            onSend={() => send(input)}
                            busy={busy}
                            disabled={!!unavailable}
                        />
                    </div>
                </aside>
            )}
        </>
    );
};

export default AssistantPanel;
